/**
 * Plan prices per currency, in MAJOR units (what a person reads, not Stripe's
 * minor units). GENERATED by scripts/gen-plan-prices.mjs from the live Stripe
 * prices — do not edit by hand; run the script and commit the result.
 *
 * Guarded by scripts/check-price-parity.mjs in `npm run verify`, so a price
 * changed in Stripe and not regenerated here fails the build rather than
 * quietly showing a customer one number and charging another.
 *
 * Zero-decimal currencies (JPY) are whole units here and in Stripe alike.
 */

export type PlanId = 'lite' | 'standard' | 'premium' | 'pro' | 'enterprise' | 'enterprise_duo';
export type BillingPeriod = 'monthly' | 'yearly' | 'onetime';

export const PLAN_PRICES: Record<PlanId, Partial<Record<BillingPeriod, Record<string, number>>>> = {
  enterprise_duo: {
    yearly: {
      eur: 490,
      aed: 2089,
      aud: 799,
      brl: 2989,
      cad: 769,
      chf: 479,
      czk: 12089,
      dkk: 3789,
      gbp: 439,
      inr: 55899,
      jpy: 90000,
      mxn: 9789,
      nok: 5489,
      pln: 2189,
      sar: 2089,
      sek: 5489,
      try: 27689,
      usd: 569,
      zar: 9089
    },
    monthly: {
      eur: 49,
      aed: 209,
      aud: 80.99,
      brl: 299,
      cad: 77.99,
      chf: 48.99,
      czk: 1209,
      dkk: 379,
      gbp: 44.99,
      inr: 5599,
      jpy: 9000,
      mxn: 979,
      nok: 549,
      pln: 219,
      sar: 209,
      sek: 549,
      try: 2769,
      usd: 57.99,
      zar: 909
    }
  },
  premium: {
    onetime: {
      eur: 120,
      aed: 519,
      aud: 189,
      brl: 719,
      cad: 189,
      chf: 119.9,
      czk: 2999,
      dkk: 909,
      gbp: 107.9,
      inr: 12999,
      jpy: 21500,
      mxn: 2399,
      nok: 1389,
      pln: 529,
      sar: 519,
      sek: 1369,
      try: 6699,
      usd: 139,
      zar: 2299
    },
    monthly: {
      eur: 10,
      aed: 42.99,
      aud: 15.99,
      brl: 59.99,
      cad: 15.99,
      chf: 9.99,
      czk: 249.99,
      dkk: 75.99,
      gbp: 8.99,
      inr: 1099,
      jpy: 1800,
      mxn: 199,
      nok: 115.99,
      pln: 43.99,
      sar: 42.99,
      sek: 113.99,
      try: 559,
      usd: 11.99,
      zar: 189
    },
    yearly: {
      eur: 100,
      aed: 429,
      aud: 159,
      brl: 599,
      cad: 159,
      chf: 99.9,
      czk: 2499,
      dkk: 759,
      gbp: 89.9,
      inr: 10999,
      jpy: 18000,
      mxn: 1999,
      nok: 1159,
      pln: 439,
      sar: 429,
      sek: 1139,
      try: 5599,
      usd: 119,
      zar: 1899
    }
  },
  standard: {
    monthly: {
      eur: 5,
      aed: 20.99,
      aud: 7.99,
      brl: 29.99,
      cad: 7.99,
      chf: 4.99,
      czk: 124.99,
      dkk: 37.99,
      gbp: 4.49,
      inr: 549,
      jpy: 900,
      mxn: 98.99,
      nok: 57.99,
      pln: 21.99,
      sar: 21.99,
      sek: 56.99,
      try: 279,
      usd: 5.99,
      zar: 93.99
    },
    yearly: {
      eur: 50,
      aed: 209,
      aud: 79.99,
      brl: 299,
      cad: 79.99,
      chf: 49.9,
      czk: 1249,
      dkk: 379,
      gbp: 44.9,
      inr: 5499,
      jpy: 9000,
      mxn: 989,
      nok: 579,
      pln: 219,
      sar: 219,
      sek: 569,
      try: 2799,
      usd: 59.99,
      zar: 939
    }
  },
  enterprise: {
    onetime: {
      eur: 1800,
      aed: 7699,
      aud: 2999,
      brl: 10999,
      cad: 2899,
      chf: 1799,
      czk: 44999,
      dkk: 13999,
      gbp: 1699,
      inr: 203999,
      jpy: 330000,
      mxn: 35999,
      nok: 19999,
      pln: 7999,
      sar: 7799,
      sek: 19999,
      try: 101999,
      usd: 2099,
      zar: 33999
    },
    yearly: {
      eur: 1500,
      aed: 6399,
      aud: 2499,
      brl: 9229,
      cad: 2399,
      chf: 1439,
      czk: 37100,
      dkk: 11500,
      gbp: 1309,
      inr: 169500,
      jpy: 275000,
      mxn: 29999,
      nok: 16700,
      pln: 6619,
      sar: 6499,
      sek: 16900,
      try: 84900,
      usd: 1779,
      zar: 27999
    },
    monthly: {
      eur: 150,
      aed: 639,
      aud: 249,
      brl: 929,
      cad: 239,
      chf: 149,
      czk: 3709,
      dkk: 1149,
      gbp: 139,
      inr: 17000,
      jpy: 27500,
      mxn: 2999,
      nok: 1669,
      pln: 669,
      sar: 649,
      sek: 1689,
      try: 8489,
      usd: 179,
      zar: 2799
    }
  },
  pro: {
    onetime: {
      eur: 228,
      aed: 969,
      aud: 369,
      brl: 1399,
      cad: 369,
      chf: 229,
      czk: 5599,
      dkk: 1799,
      gbp: 203,
      inr: 25999,
      jpy: 42000,
      mxn: 4499,
      nok: 2599,
      pln: 999,
      sar: 979,
      sek: 2599,
      try: 12999,
      usd: 279,
      zar: 4299
    },
    yearly: {
      eur: 190,
      aed: 809,
      aud: 309,
      brl: 1169,
      cad: 309,
      chf: 189,
      czk: 4689,
      dkk: 1449,
      gbp: 169,
      inr: 21500,
      jpy: 35000,
      mxn: 3799,
      nok: 2119,
      pln: 839,
      sar: 819,
      sek: 2139,
      try: 10800,
      usd: 229,
      zar: 3599
    },
    monthly: {
      eur: 19,
      aed: 80.99,
      aud: 30.99,
      brl: 119,
      cad: 30.99,
      chf: 18.99,
      czk: 469,
      dkk: 149,
      gbp: 16.99,
      inr: 2149,
      jpy: 3500,
      mxn: 379,
      nok: 219,
      pln: 83.99,
      sar: 81.99,
      sek: 219,
      try: 1079,
      usd: 22.99,
      zar: 359
    }
  },
  lite: {
    monthly: {
      eur: 2,
      aed: 8.99,
      aud: 2.99,
      brl: 11.99,
      cad: 2.99,
      chf: 1.99,
      czk: 49.99,
      dkk: 14.99,
      gbp: 1.79,
      inr: 189,
      jpy: 400,
      mxn: 38.99,
      nok: 22.99,
      pln: 8.99,
      sar: 8.99,
      sek: 22.99,
      try: 69.99,
      usd: 2.19,
      zar: 37.99
    }
  }
} as const;

/** Currencies present on every generated price. */
export const PRICED_CURRENCIES = ['AED', 'AUD', 'BRL', 'CAD', 'CHF', 'CZK', 'DKK', 'EUR', 'GBP', 'INR', 'JPY', 'MXN', 'NOK', 'PLN', 'SAR', 'SEK', 'TRY', 'USD', 'ZAR'] as const;

const ZERO_DECIMAL = new Set(['JPY']);

/**
 * Amount for a plan in the requested currency, falling back to euro for a
 * currency we do not price in. Returns null when the plan has no such period
 * (Lite is monthly only; Standard has no one-time licence).
 */
export function planPrice(plan: PlanId, period: BillingPeriod, currency: string): number | null {
  const amounts = PLAN_PRICES[plan]?.[period];
  if (!amounts) return null;
  const key = currency.toLowerCase();
  const value = amounts[key] ?? amounts.eur;
  return value ?? null;
}

/** Whole units, no minor part: yen prices must never render as "18000.00". */
export function isZeroDecimal(currency: string): boolean {
  return ZERO_DECIMAL.has(currency.toUpperCase());
}

export type AddonId = 'tenders' | 'tenders_list';

/**
 * Tender Intelligence. `tenders` is the beta price actually charged;
 * `tenders_list` is the standard price it is discounted from, shown struck
 * through. Both are real Stripe prices, so the discount a reader computes from
 * the two numbers is one we can honour.
 */
export const ADDON_PRICES: Record<AddonId, Partial<Record<BillingPeriod, Record<string, number>>>> = {
  tenders_list: {
    yearly: {
      eur: 200,
      aed: 859,
      aud: 319,
      brl: 1199,
      cad: 319,
      chf: 199,
      czk: 4999,
      dkk: 1499,
      gbp: 179,
      inr: 21999,
      jpy: 36000,
      mxn: 3999,
      nok: 2399,
      pln: 879,
      sar: 859,
      sek: 2199,
      try: 10999,
      usd: 239,
      zar: 3799
    },
    monthly: {
      eur: 20,
      aed: 85.99,
      aud: 31.99,
      brl: 119,
      cad: 31.99,
      chf: 19.99,
      czk: 499,
      dkk: 149,
      gbp: 17.99,
      inr: 2199,
      jpy: 3600,
      mxn: 399,
      nok: 239,
      pln: 87.99,
      sar: 85.99,
      sek: 219,
      try: 1099,
      usd: 23.99,
      zar: 379
    }
  },
  tenders: {
    yearly: {
      eur: 100,
      aed: 429,
      aud: 159,
      brl: 609,
      cad: 159,
      chf: 99.99,
      czk: 2499,
      dkk: 769,
      gbp: 89.99,
      inr: 10999,
      jpy: 18000,
      mxn: 1999,
      nok: 1199,
      pln: 439,
      sar: 429,
      sek: 1099,
      try: 5699,
      usd: 119,
      zar: 1899
    },
    monthly: {
      eur: 10,
      aed: 42.99,
      aud: 15.99,
      brl: 60.99,
      cad: 15.99,
      chf: 9.99,
      czk: 249,
      dkk: 76.99,
      gbp: 8.99,
      inr: 1099,
      jpy: 1800,
      mxn: 199,
      nok: 119,
      pln: 43.99,
      sar: 42.99,
      sek: 109,
      try: 569,
      usd: 11.99,
      zar: 189
    }
  }
} as const;

export function addonPrice(addon: AddonId, period: BillingPeriod, currency: string): number | null {
  const amounts = ADDON_PRICES[addon]?.[period];
  if (!amounts) return null;
  return amounts[currency.toLowerCase()] ?? amounts.eur ?? null;
}

/** Extra Pro editor seats: one flat rate per seat. */
export const SEAT_PRICES: Record<string, Partial<Record<BillingPeriod, Record<string, number>>>> = {
  enterprise_duo_seat: {
    yearly: {
      eur: 190,
      aed: 809,
      aud: 309,
      brl: 1169,
      cad: 309,
      chf: 189,
      czk: 4689,
      dkk: 1449,
      gbp: 169,
      inr: 21500,
      jpy: 35000,
      mxn: 3799,
      nok: 2119,
      pln: 839,
      sar: 819,
      sek: 2139,
      try: 10800,
      usd: 229,
      zar: 3599
    },
    monthly: {
      eur: 19,
      aed: 80.99,
      aud: 30.99,
      brl: 119,
      cad: 30.99,
      chf: 18.99,
      czk: 469,
      dkk: 149,
      gbp: 16.99,
      inr: 2149,
      jpy: 3500,
      mxn: 379,
      nok: 219,
      pln: 83.99,
      sar: 81.99,
      sek: 219,
      try: 1079,
      usd: 22.99,
      zar: 359
    }
  },
  pro_seat: {
    yearly: {
      eur: 100,
      aed: 429,
      aud: 159,
      brl: 619,
      cad: 159,
      chf: 95.99,
      czk: 2469,
      dkk: 769,
      gbp: 87.99,
      inr: 11300,
      jpy: 18000,
      mxn: 1999,
      nok: 1119,
      pln: 449,
      sar: 429,
      sek: 1129,
      try: 5659,
      usd: 119,
      zar: 1899
    },
    monthly: {
      eur: 10,
      aed: 42.99,
      aud: 15.99,
      brl: 61.99,
      cad: 15.99,
      chf: 9.99,
      czk: 249,
      dkk: 76.99,
      gbp: 8.99,
      inr: 1139,
      jpy: 1800,
      mxn: 199,
      nok: 119,
      pln: 44.99,
      sar: 42.99,
      sek: 119,
      try: 569,
      usd: 11.99,
      zar: 189
    }
  }
} as const;

export function seatPrice(period: BillingPeriod, currency: string, family: string = 'pro_seat'): number | null {
  const amounts = SEAT_PRICES[family]?.[period];
  if (!amounts) return null;
  return amounts[currency.toLowerCase()] ?? amounts.eur ?? null;
}

/**
 * Enterprise seats are graduated: the ladder must be read per currency, never
 * converted from euro at display time, or the volume discount drifts.
 * `up_to: null` is the open-ended top tier.
 */
export type SeatTier = { up_to: number | null; unit: number };
export const SEAT_TIERS: Record<string, Partial<Record<BillingPeriod, Record<string, SeatTier[]>>>> = {
  enterprise_seat: {
    yearly: {
      eur: [
        {
          up_to: 10,
          unit: 90
        },
        {
          up_to: 25,
          unit: 70
        },
        {
          up_to: 50,
          unit: 50
        },
        {
          up_to: null,
          unit: 40
        }
      ],
      aed: [
        {
          up_to: 10,
          unit: 380
        },
        {
          up_to: 25,
          unit: 300
        },
        {
          up_to: 50,
          unit: 210
        },
        {
          up_to: null,
          unit: 170
        }
      ],
      aud: [
        {
          up_to: 10,
          unit: 150
        },
        {
          up_to: 25,
          unit: 110
        },
        {
          up_to: 50,
          unit: 80
        },
        {
          up_to: null,
          unit: 70
        }
      ],
      brl: [
        {
          up_to: 10,
          unit: 550
        },
        {
          up_to: 25,
          unit: 430
        },
        {
          up_to: 50,
          unit: 310
        },
        {
          up_to: null,
          unit: 250
        }
      ],
      cad: [
        {
          up_to: 10,
          unit: 140
        },
        {
          up_to: 25,
          unit: 110
        },
        {
          up_to: 50,
          unit: 80
        },
        {
          up_to: null,
          unit: 60
        }
      ],
      chf: [
        {
          up_to: 10,
          unit: 90
        },
        {
          up_to: 25,
          unit: 70
        },
        {
          up_to: 50,
          unit: 50
        },
        {
          up_to: null,
          unit: 40
        }
      ],
      czk: [
        {
          up_to: 10,
          unit: 2290
        },
        {
          up_to: 25,
          unit: 1790
        },
        {
          up_to: 50,
          unit: 1290
        },
        {
          up_to: null,
          unit: 1000
        }
      ],
      dkk: [
        {
          up_to: 10,
          unit: 690
        },
        {
          up_to: 25,
          unit: 530
        },
        {
          up_to: 50,
          unit: 380
        },
        {
          up_to: null,
          unit: 310
        }
      ],
      gbp: [
        {
          up_to: 10,
          unit: 80
        },
        {
          up_to: 25,
          unit: 60
        },
        {
          up_to: 50,
          unit: 40
        },
        {
          up_to: null,
          unit: 30
        }
      ],
      inr: [
        {
          up_to: 10,
          unit: 9990
        },
        {
          up_to: 25,
          unit: 7890
        },
        {
          up_to: 50,
          unit: 5690
        },
        {
          up_to: null,
          unit: 4490
        }
      ],
      jpy: [
        {
          up_to: 10,
          unit: 17000
        },
        {
          up_to: 25,
          unit: 13000
        },
        {
          up_to: 50,
          unit: 9000
        },
        {
          up_to: null,
          unit: 7000
        }
      ],
      mxn: [
        {
          up_to: 10,
          unit: 1790
        },
        {
          up_to: 25,
          unit: 1390
        },
        {
          up_to: 50,
          unit: 990
        },
        {
          up_to: null,
          unit: 790
        }
      ],
      nok: [
        {
          up_to: 10,
          unit: 1090
        },
        {
          up_to: 25,
          unit: 810
        },
        {
          up_to: 50,
          unit: 580
        },
        {
          up_to: null,
          unit: 460
        }
      ],
      pln: [
        {
          up_to: 10,
          unit: 400
        },
        {
          up_to: 25,
          unit: 310
        },
        {
          up_to: 50,
          unit: 220
        },
        {
          up_to: null,
          unit: 180
        }
      ],
      sar: [
        {
          up_to: 10,
          unit: 390
        },
        {
          up_to: 25,
          unit: 300
        },
        {
          up_to: 50,
          unit: 220
        },
        {
          up_to: null,
          unit: 170
        }
      ],
      sek: [
        {
          up_to: 10,
          unit: 990
        },
        {
          up_to: 25,
          unit: 800
        },
        {
          up_to: 50,
          unit: 570
        },
        {
          up_to: null,
          unit: 460
        }
      ],
      try: [
        {
          up_to: 10,
          unit: 5090
        },
        {
          up_to: 25,
          unit: 3990
        },
        {
          up_to: 50,
          unit: 2790
        },
        {
          up_to: null,
          unit: 2290
        }
      ],
      usd: [
        {
          up_to: 10,
          unit: 110
        },
        {
          up_to: 25,
          unit: 80
        },
        {
          up_to: 50,
          unit: 60
        },
        {
          up_to: null,
          unit: 50
        }
      ],
      zar: [
        {
          up_to: 10,
          unit: 1690
        },
        {
          up_to: 25,
          unit: 1290
        },
        {
          up_to: 50,
          unit: 940
        },
        {
          up_to: null,
          unit: 750
        }
      ]
    },
    monthly: {
      eur: [
        {
          up_to: 10,
          unit: 9
        },
        {
          up_to: 25,
          unit: 7
        },
        {
          up_to: 50,
          unit: 5
        },
        {
          up_to: null,
          unit: 4
        }
      ],
      aed: [
        {
          up_to: 10,
          unit: 38
        },
        {
          up_to: 25,
          unit: 30
        },
        {
          up_to: 50,
          unit: 21
        },
        {
          up_to: null,
          unit: 17
        }
      ],
      aud: [
        {
          up_to: 10,
          unit: 15
        },
        {
          up_to: 25,
          unit: 11
        },
        {
          up_to: 50,
          unit: 8
        },
        {
          up_to: null,
          unit: 7
        }
      ],
      brl: [
        {
          up_to: 10,
          unit: 55
        },
        {
          up_to: 25,
          unit: 43
        },
        {
          up_to: 50,
          unit: 31
        },
        {
          up_to: null,
          unit: 25
        }
      ],
      cad: [
        {
          up_to: 10,
          unit: 14
        },
        {
          up_to: 25,
          unit: 11
        },
        {
          up_to: 50,
          unit: 8
        },
        {
          up_to: null,
          unit: 6
        }
      ],
      chf: [
        {
          up_to: 10,
          unit: 9
        },
        {
          up_to: 25,
          unit: 7
        },
        {
          up_to: 50,
          unit: 5
        },
        {
          up_to: null,
          unit: 4
        }
      ],
      czk: [
        {
          up_to: 10,
          unit: 229
        },
        {
          up_to: 25,
          unit: 179
        },
        {
          up_to: 50,
          unit: 129
        },
        {
          up_to: null,
          unit: 100
        }
      ],
      dkk: [
        {
          up_to: 10,
          unit: 69
        },
        {
          up_to: 25,
          unit: 53
        },
        {
          up_to: 50,
          unit: 38
        },
        {
          up_to: null,
          unit: 31
        }
      ],
      gbp: [
        {
          up_to: 10,
          unit: 8
        },
        {
          up_to: 25,
          unit: 6
        },
        {
          up_to: 50,
          unit: 4
        },
        {
          up_to: null,
          unit: 3
        }
      ],
      inr: [
        {
          up_to: 10,
          unit: 999
        },
        {
          up_to: 25,
          unit: 789
        },
        {
          up_to: 50,
          unit: 569
        },
        {
          up_to: null,
          unit: 449
        }
      ],
      jpy: [
        {
          up_to: 10,
          unit: 1700
        },
        {
          up_to: 25,
          unit: 1300
        },
        {
          up_to: 50,
          unit: 900
        },
        {
          up_to: null,
          unit: 700
        }
      ],
      mxn: [
        {
          up_to: 10,
          unit: 179
        },
        {
          up_to: 25,
          unit: 139
        },
        {
          up_to: 50,
          unit: 99
        },
        {
          up_to: null,
          unit: 79
        }
      ],
      nok: [
        {
          up_to: 10,
          unit: 109
        },
        {
          up_to: 25,
          unit: 81
        },
        {
          up_to: 50,
          unit: 58
        },
        {
          up_to: null,
          unit: 46
        }
      ],
      pln: [
        {
          up_to: 10,
          unit: 40
        },
        {
          up_to: 25,
          unit: 31
        },
        {
          up_to: 50,
          unit: 22
        },
        {
          up_to: null,
          unit: 18
        }
      ],
      sar: [
        {
          up_to: 10,
          unit: 39
        },
        {
          up_to: 25,
          unit: 30
        },
        {
          up_to: 50,
          unit: 22
        },
        {
          up_to: null,
          unit: 17
        }
      ],
      sek: [
        {
          up_to: 10,
          unit: 99
        },
        {
          up_to: 25,
          unit: 80
        },
        {
          up_to: 50,
          unit: 57
        },
        {
          up_to: null,
          unit: 46
        }
      ],
      try: [
        {
          up_to: 10,
          unit: 509
        },
        {
          up_to: 25,
          unit: 399
        },
        {
          up_to: 50,
          unit: 279
        },
        {
          up_to: null,
          unit: 229
        }
      ],
      usd: [
        {
          up_to: 10,
          unit: 11
        },
        {
          up_to: 25,
          unit: 8
        },
        {
          up_to: 50,
          unit: 6
        },
        {
          up_to: null,
          unit: 5
        }
      ],
      zar: [
        {
          up_to: 10,
          unit: 169
        },
        {
          up_to: 25,
          unit: 129
        },
        {
          up_to: 50,
          unit: 94
        },
        {
          up_to: null,
          unit: 75
        }
      ]
    }
  }
} as const;

export function seatTiers(period: BillingPeriod, currency: string): SeatTier[] {
  const byCur = SEAT_TIERS.enterprise_seat?.[period];
  if (!byCur) return [];
  return byCur[currency.toLowerCase()] ?? byCur.eur ?? [];
}

/** Graduated total for `seats` extra seats, in the reader's currency. */
export function seatCost(seats: number, period: BillingPeriod, currency: string): number {
  const tiers = seatTiers(period, currency);
  let remaining = Math.max(0, seats);
  let prev = 0;
  let total = 0;
  for (const t of tiers) {
    if (remaining <= 0) break;
    const cap = t.up_to ?? Infinity;
    const inTier = Math.min(remaining, cap - prev);
    total += inTier * t.unit;
    remaining -= inTier;
    prev = cap;
  }
  return total;
}
